import { ReactElement, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  RichTextDisplay,
  RichTextEditorInput,
  Row,
  SheetSectionContainer,
  SheetSectionEmptyText,
  SheetSectionHeader,
} from "@/components";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { useUpdateProfileUser } from "@/hooks/mutations/useUpdateProfileUser";
import { cn } from "@/lib/utils";

const professionalSummarySchema = z.object({
  professionalSummary: z.string(),
});

const highlightProfessionalSummary = (professionalSummary: string) =>
  professionalSummary;

const ProfessionalSummaryWrapper = ({
  children,
  containerClasses,
  editControls,
  isEmpty,
  titleActions,
}: {
  children: ReactElement;
  containerClasses?: string;
  editControls?: ReactElement;
  isEmpty?: boolean;
  titleActions?: ReactElement;
}) => (
  <SheetSectionContainer className={cn(containerClasses, "gap-3")}>
    <Row className="h-6 items-center justify-between">
      <Row className="items-center gap-1">
        <SheetSectionHeader isEmpty={isEmpty} title="Professional Summary" />
        {titleActions}
      </Row>
      {editControls}
    </Row>
    {children}
  </SheetSectionContainer>
);

const ProfessionalSummaryContent = ({
  highlightRelevantTerms = false,
  professionalSummary,
}: {
  highlightRelevantTerms?: boolean;
  professionalSummary: string | null;
}) => {
  if (!professionalSummary) {
    return (
      <SheetSectionEmptyText text="This candidate hasn't added a professional summary." />
    );
  }

  return (
    <RichTextDisplay
      className="text-sm font-light leading-relaxed text-[#121212] dark:text-[#FAFAFA]"
      richText={
        highlightRelevantTerms
          ? highlightProfessionalSummary(professionalSummary)
          : professionalSummary
      }
    />
  );
};

const ProfessionalSummaryForm = ({
  form,
}: {
  form: ReturnType<typeof useForm<z.infer<typeof professionalSummarySchema>>>;
}) => (
  <Form {...form}>
    <form
      className="flex w-full flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <FormField
        control={form.control}
        name="professionalSummary"
        render={({ field }) => (
          <FormItem className="text-slate-700">
            <FormControl>
              <RichTextEditorInput
                mode={{
                  onChange: field.onChange,
                  type: "html",
                  value: field.value,
                }}
                className="min-h-72"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </form>
  </Form>
);

export const ProfessionalSummaryEdit = ({
  containerClasses,
  professionalSummary,
  titleActions,
  userId,
}: {
  containerClasses?: string;
  professionalSummary: string | null;
  titleActions?: ReactElement;
  userId: number;
}) => {
  const [isShowingUpdateForm, setIsShowingUpdateForm] = useState(true);
  const editorRef = useRef<HTMLDivElement>(null);

  const { mutate: updateUser } = useUpdateProfileUser();

  const form = useForm<z.infer<typeof professionalSummarySchema>>({
    defaultValues: {
      professionalSummary: professionalSummary ?? "",
    },
    resolver: zodResolver(professionalSummarySchema),
  });

  const handleSubmit = (data: z.infer<typeof professionalSummarySchema>) => {
    const updateData = {
      professionalSummary: data.professionalSummary || null,
    };

    updateUser(
      {
        userId,
        data: updateData,
      },
      {
        onSuccess: (updatedUser) => {
          handleSuccess(updatedUser.professionalSummary);
        },
      },
    );
  };

  // useUpdateProfileUser updates the sheet-data cache at the hook level, so
  // success only manages local form state.
  const handleSuccess = (updatedProfessionalSummary: string | null) => {
    form.reset({
      professionalSummary: updatedProfessionalSummary ?? "",
    });
    setIsShowingUpdateForm(true);
  };

  const handleEdit = () => {
    setIsShowingUpdateForm(true);
  };
  const handleSave = () => {
    void form.handleSubmit(handleSubmit)();
    setIsShowingUpdateForm(true);
  };

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (isShowingUpdateForm && !editorRef.current?.contains(target)) {
        handleSave();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isShowingUpdateForm, handleSave]);

  return (
    <ProfessionalSummaryWrapper
      containerClasses={containerClasses}
      // If we're showing the update form, we don't want to show the "empty" styles.
      isEmpty={!professionalSummary && !isShowingUpdateForm}
      titleActions={titleActions}
    >
      {isShowingUpdateForm ? (
        <Row
          className="w-full motion-safe:duration-300 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1"
          ref={editorRef}
        >
          <ProfessionalSummaryForm form={form} />
        </Row>
      ) : (
        <Row
          className="-mx-2 w-[calc(100%+1rem)] cursor-text rounded-md px-2 py-1 transition-colors hover:bg-slate-100 motion-safe:duration-300 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1"
          onClick={handleEdit}
        >
          <ProfessionalSummaryContent
            professionalSummary={professionalSummary}
          />
        </Row>
      )}
    </ProfessionalSummaryWrapper>
  );
};

export const ProfessionalSummaryView = ({
  containerClasses,
  headerActions,
  highlightRelevantTerms,
  professionalSummary,
  titleActions,
}: {
  containerClasses?: string;
  headerActions?: ReactElement;
  highlightRelevantTerms?: boolean;
  professionalSummary: string | null;
  titleActions?: ReactElement;
}) => {
  return (
    <ProfessionalSummaryWrapper
      containerClasses={containerClasses}
      editControls={headerActions}
      isEmpty={!professionalSummary}
      titleActions={titleActions}
    >
      <ProfessionalSummaryContent
        highlightRelevantTerms={highlightRelevantTerms}
        professionalSummary={professionalSummary}
      />
    </ProfessionalSummaryWrapper>
  );
};

export const SheetProfessionalSummary = ({
  containerClasses,
  headerActions,
  highlightRelevantTerms = false,
  isEditing = false,
  professionalSummary,
  titleActions,
  userId,
}: {
  containerClasses?: string;
  headerActions?: ReactElement;
  highlightRelevantTerms?: boolean;
  isEditing?: boolean;
  professionalSummary: string | null;
  titleActions?: ReactElement;
  // Present when the profile is editable.
  userId?: number;
}) => {
  if (isEditing && userId !== undefined) {
    return (
      <ProfessionalSummaryEdit
        containerClasses={containerClasses}
        professionalSummary={professionalSummary}
        titleActions={titleActions}
        userId={userId}
      />
    );
  }

  return (
    <ProfessionalSummaryView
      containerClasses={containerClasses}
      headerActions={headerActions}
      highlightRelevantTerms={highlightRelevantTerms}
      professionalSummary={professionalSummary}
      titleActions={titleActions}
    />
  );
};
